import type { Axios } from "axios";
import { function as function_, taskEither } from "fp-ts";

import { UserEntity, UserServicePorts } from "../../model";

import { URLS } from "./consts";
import { AssignRoles, Create, Delete, Get, GetAll, GetPaginated, Update } from "./ios";
import { mapDtoToEntity } from "./mappers";

import { Request, Response } from "~/shared/api";
import {
  AuthenticationError,
  NotFoundError,
  UnexpectedError,
  UniqueKeyViolationError,
} from "~/shared/error";
import { Pagination } from "~/shared/zod";

class ApiProvider extends UserServicePorts.Provider {
  private readonly axios: Axios;

  constructor(axios: Axios) {
    super();
    this.axios = axios;
  }

  override create({
    phone,
    ...params
  }: UserServicePorts.ProviderIos.Create.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError | UniqueKeyViolationError<UserEntity.Constraint>,
    UserServicePorts.ProviderIos.Common.Out
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(URLS.create, {
            ...params,
            phoneNumber: phone,
          }),
        Create.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 201) return taskEither.right(mapDtoToEntity(body.data));

        if (status === 400 && body.errorDetails.codeErrors.includes("EMAIL_ALREADY_EXISTS"))
          return taskEither.left(
            new UniqueKeyViolationError(UserEntity.Constraint.UNIQUE_USER_EMAIL),
          );

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override update({
    phone,
    ...params
  }: UserServicePorts.ProviderIos.Update.In): taskEither.TaskEither<
    | UnexpectedError
    | AuthenticationError
    | NotFoundError
    | UniqueKeyViolationError<UserEntity.Constraint>,
    UserServicePorts.ProviderIos.Common.Out
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.put(URLS.update, {
            ...params,
            phoneNumber: phone,
          }),
        Update.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200) return taskEither.right(mapDtoToEntity(body.data));

        if (
          status === 400 &&
          body.errorDetails.codeErrors.includes(`Username '${params.email}' is already taken.`)
        )
          return taskEither.left(
            new UniqueKeyViolationError(UserEntity.Constraint.UNIQUE_USER_EMAIL),
          );

        if (status === 401) return taskEither.left(new AuthenticationError());

        if (status === 404) return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override delete({
    id,
  }: UserServicePorts.ProviderIos.Delete.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError | NotFoundError,
    void
  > {
    return function_.pipe(
      Response.process(() => this.axios.delete(URLS.delete(id)), Delete.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 204) return taskEither.right(void 0);

        if (status === 401) return taskEither.left(new AuthenticationError());

        if (status === 404) return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override assignRoles({
    id,
    ...params
  }: UserServicePorts.ProviderIos.AssignRoles.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError | UserServicePorts.ProviderIos.AssignRoles.Error,
    void
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(URLS.assignRoles, {
            userId: id,
            ...params,
          }),
        AssignRoles.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(void 0);

        if (status === 400) {
          const { codeErrors } = response.body.errorDetails;
          if (codeErrors.includes("USER_NOT_FOUND"))
            return taskEither.left(
              new UserServicePorts.ProviderIos.AssignRoles.Error(
                UserServicePorts.ProviderIos.AssignRoles.ErrorReason.USER_NOT_FOUND,
              ),
            );
          if (codeErrors.includes("ROLE_NOT_FOUND"))
            return taskEither.left(
              new UserServicePorts.ProviderIos.AssignRoles.Error(
                UserServicePorts.ProviderIos.AssignRoles.ErrorReason.ROLE_S_NOT_FOUND,
              ),
            );
        }

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override get({
    id,
  }: UserServicePorts.ProviderIos.Get.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError | NotFoundError,
    UserServicePorts.ProviderIos.Common.Out
  > {
    return function_.pipe(
      Response.process(() => this.axios.get(URLS.get(id)), Get.zRes),

      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(mapDtoToEntity(response.body.data));

        if (status === 401) return taskEither.left(new AuthenticationError());

        if (status === 404) return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override getAll(): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    UserServicePorts.ProviderIos.GetAll.Out
  > {
    return function_.pipe(
      Response.process(() => this.axios.get(URLS.getAll), GetAll.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) {
          return taskEither.right(response.body.data.map(mapDtoToEntity));
        }

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override getPaginated(
    params: UserServicePorts.ProviderIos.GetPaginated.In,
  ): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    UserServicePorts.ProviderIos.GetPaginated.Out
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(
            URLS.getPaginated,
            Request.ListParams.mapListParams(params, GetPaginated.MAP_OF_FIELDS_TO_SORT_BY),
          ),
        GetPaginated.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) {
          const {
            data: { items, totalCount },
          } = response.body;
          const {
            success,
            data: pageMeta,
            error,
          } = Pagination.Meta.zSchema.safeParse({
            options: params.pagination,
            totalItems: totalCount,
          });

          if (!success) return taskEither.left(new UnexpectedError(error));

          return taskEither.right({
            items: items.map(mapDtoToEntity),
            meta: pageMeta,
          } satisfies UserServicePorts.ProviderIos.GetPaginated.Out);
        }

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }
}

export { ApiProvider };
